import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';
import { TradingOfferOperation } from 'types';
import { TradingOfferStatusCounts } from '../../../types/TradingOffer';
import { getTradingOfferOperations } from '../redux/tradingOfferOperationActions';

type UseGetTradingOfferOperationsProps = {
  societyId?: string;
  page?: number;
  rowsPerPage?: number;
  refetchKey?: number;
};

export const useGetTradingOfferOperations = ({
  societyId,
  page = 1,
  rowsPerPage = 10,
  refetchKey = 0,
}: UseGetTradingOfferOperationsProps) => {
  const [tradingOfferOperations, setTradingOfferOperations] = useState<
    TradingOfferOperation[]
  >([]);
  const [total, setTotal] = useState(0);
  const [pendingOperationsCount, setPendingOperationsCount] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchTradingOfferOperations = useCallback(
    async (controller: AbortController) => {
      if (!societyId) return;

      try {
        setIsLoading(true);

        const {
          data,
          total: totalResult,
          pendingOperationsCount: pendingOperationsCountResult,
        } = await store.dispatch(
          getTradingOfferOperations({
            societyId,
            params: { page, items: rowsPerPage },
            signal: controller.signal,
          })
        );

        if (!controller.signal.aborted) {
          setTradingOfferOperations(data);
          setTotal(totalResult);
          setPendingOperationsCount(pendingOperationsCountResult);
          setIsLoading(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsError(true);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, rowsPerPage, societyId, refetchKey]
  );

  useEffect(() => {
    const controller = new AbortController();

    fetchTradingOfferOperations(controller);

    return () => controller.abort();
  }, [fetchTradingOfferOperations]);

  return {
    data: { tradingOfferOperations, total, pendingOperationsCount },
    isLoading,
    isError,
  };
};
